import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Plus, Eye, Check, Pencil, Power } from 'lucide-react';
import { Button, Card, Badge, toast, Tabs, TabsList, TabsTrigger } from '@employer-on-record/ui';
import type { LeaveRequestDTO, ReimbursementClaimDTO, EmployeeDTO, UserDTO } from '@employer-on-record/shared';
import { companiesApi, employeesApi, adminApi, leaveApi, reimbursementsApi, employeesAdminApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { PageHead } from '@/components/PageHead';
import { Avatar } from '@/components/Avatar';
import { ApprovalProgress } from '@/components/ApprovalProgress';
import { LeaveDetailModal } from '@/components/modals/LeaveDetailModal';
import { ReimbursementDetailModal } from '@/components/modals/ReimbursementDetailModal';
import { UserFormDrawer } from '@/components/modals/UserFormDrawer';
import { EmployeeFormDrawer } from '@/components/modals/EmployeeFormDrawer';
import { formatIDR, formatDateShort, formatDateRange, LABEL_BY_LEAVE_TYPE, LABEL_BY_REIMBURSEMENT_CATEGORY, LABEL_BY_REIMBURSEMENT_STATUS, TONE_BY_LEAVE_STATUS, TONE_BY_REIMBURSEMENT_STATUS } from '@/lib/utils';

type Tab = 'employees' | 'hr' | 'approvers' | 'leave' | 'reimbursement';

type UserRow = UserDTO & { company?: { id: string; name: string } | null; department?: string | null; position?: string | null };

export default function CompanyDetailPage() {
	const { id = '' } = useParams();
	const qc = useQueryClient();
	const role = useAuthStore((s) => s.user?.role);
	const isSuperAdmin = role === 'super_admin';

	const [tab, setTab] = useState<Tab>('employees');
	const [hrDrawer, setHrDrawer] = useState<{ open: boolean; mode: 'create' | 'edit'; user: UserRow | null }>({ open: false, mode: 'create', user: null });
	const [approverDrawer, setApproverDrawer] = useState<{ open: boolean; mode: 'create' | 'edit'; user: UserRow | null }>({ open: false, mode: 'create', user: null });
	const [employeeDrawer, setEmployeeDrawer] = useState<{ open: boolean; mode: 'create' | 'edit'; employee: EmployeeDTO | null }>({ open: false, mode: 'create', employee: null });
	const [detailLeave, setDetailLeave] = useState<LeaveRequestDTO | null>(null);
	const [detailReimb, setDetailReimb] = useState<ReimbursementClaimDTO | null>(null);

	const { data: companyResp } = useQuery({ queryKey: ['admin-company', id], queryFn: () => companiesApi.get(id), enabled: !!id });
	const { data: employees } = useQuery({ queryKey: ['admin-company-employees', id], queryFn: () => employeesApi.list(id), enabled: !!id });
	const { data: hrUsers } = useQuery({ queryKey: ['admin-company-hr', id], queryFn: () => adminApi.hrUsers(id), enabled: !!id });
	const { data: approvers } = useQuery({ queryKey: ['admin-company-approvers', id], queryFn: () => adminApi.approverUsers(id), enabled: !!id });
	const { data: leaves } = useQuery({ queryKey: ['admin-company-leave', id], queryFn: () => leaveApi.list(id), enabled: !!id && tab === 'leave' });
	const { data: claims } = useQuery({ queryKey: ['admin-company-reimb', id], queryFn: () => reimbursementsApi.list(id), enabled: !!id && tab === 'reimbursement' });

	const company = companyResp?.data;

	const reimburse = useMutation({
		mutationFn: (claimId: string) => reimbursementsApi.reimburse(claimId),
		onSuccess: () => {
			toast('Marked as reimbursement paid');
			qc.invalidateQueries({ queryKey: ['admin-company-reimb', id] });
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	const setActive = useMutation({
		mutationFn: ({ userId, isActive }: { userId: string; isActive: boolean }) => adminApi.setUserActive(userId, isActive),
		onSuccess: () => {
			toast('User updated');
			qc.invalidateQueries({ queryKey: ['admin-company-hr', id] });
			qc.invalidateQueries({ queryKey: ['admin-company-approvers', id] });
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	const setEmployeeStatus = useMutation({
		mutationFn: ({ employeeId, status }: { employeeId: string; status: 'active' | 'terminated' }) =>
			employeesAdminApi.update(employeeId, { status }),
		onSuccess: () => {
			toast('Employee updated');
			qc.invalidateQueries({ queryKey: ['admin-company-employees', id] });
		},
		onError: (err) => toast((err as Error).message, 'destructive')
	});

	if (!company) return <div className="text-sm text-muted-foreground">Loading…</div>;

	return (
		<>
			<div className="mb-2">
				<Button asChild variant="ghost" size="sm">
					<Link to="/companies"><ChevronLeft className="h-3 w-3" /> Back to companies</Link>
				</Button>
			</div>
			<PageHead
				title={company.name}
				subtitle={`${company.industry || 'Client company'} · Leave ${company.leaveApprovalLevels} levels · Reimburse ${company.reimbursementApprovalLevels} levels`}
			/>

			<LeaveDetailModal open={!!detailLeave} onClose={() => setDetailLeave(null)} request={detailLeave} />
			<ReimbursementDetailModal open={!!detailReimb} onClose={() => setDetailReimb(null)} claim={detailReimb} />

			<Card>
				<div className="px-5 pt-1">
					<Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
						<TabsList>
							<TabsTrigger value="employees">Employees <span className="ml-1 text-muted-subtle font-medium">{employees?.data.length ?? 0}</span></TabsTrigger>
							<TabsTrigger value="hr">HR users <span className="ml-1 text-muted-subtle font-medium">{hrUsers?.data.length ?? 0}</span></TabsTrigger>
							<TabsTrigger value="approvers">Approvals <span className="ml-1 text-muted-subtle font-medium">{approvers?.data.length ?? 0}</span></TabsTrigger>
							<TabsTrigger value="leave">Leave</TabsTrigger>
							<TabsTrigger value="reimbursement">Reimbursement</TabsTrigger>
						</TabsList>
					</Tabs>
				</div>

				{tab === 'employees' && (
					<>
						{isSuperAdmin && (
							<div className="px-5 py-3 border-b flex items-center justify-end">
								<Button onClick={() => setEmployeeDrawer({ open: true, mode: 'create', employee: null })}><Plus className="h-3 w-3" /> Add employee</Button>
							</div>
						)}
						<table className="w-full text-[13px]">
							<thead>
								<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
									<th className="text-left px-4 py-2.5 font-semibold">Employee</th>
									<th className="text-left px-4 py-2.5 font-semibold">Department</th>
									<th className="text-left px-4 py-2.5 font-semibold">Position</th>
									<th className="text-left px-4 py-2.5 font-semibold">Start</th>
									<th className="text-left px-4 py-2.5 font-semibold">Status</th>
									{isSuperAdmin && <th />}
								</tr>
							</thead>
							<tbody>
								{(employees?.data ?? []).length === 0 && (
									<tr><td colSpan={6} className="text-center text-muted-foreground p-10">No employees yet.</td></tr>
								)}
								{(employees?.data ?? []).map((e) => (
									<tr key={e.id} className="border-b last:border-0 hover:bg-background/60">
										<td className="px-4 py-3.5">
											<div className="flex items-center gap-2.5">
												<Avatar name={e.fullName} />
												<div>
													<div className="font-medium">{e.fullName}</div>
													<div className="text-[11px] text-muted-foreground">{e.employeeNumber} · {e.email}</div>
												</div>
											</div>
										</td>
										<td className="px-4 py-3.5 text-muted-foreground">{e.department || '—'}</td>
										<td className="px-4 py-3.5 text-muted-foreground">{e.position || '—'}</td>
										<td className="px-4 py-3.5 text-muted-foreground">{formatDateShort(e.startDate)}</td>
										<td className="px-4 py-3.5"><Badge tone={e.status === 'active' ? 'green' : e.status === 'on_leave' ? 'amber' : 'gray'}>{e.status}</Badge></td>
										{isSuperAdmin && (
											<td className="px-4 py-3.5 text-right">
												<div className="inline-flex gap-1.5">
													<Button variant="secondary" size="sm" onClick={() => setEmployeeDrawer({ open: true, mode: 'edit', employee: e })}>
														<Pencil className="h-3 w-3" /> Edit
													</Button>
													<Button variant="ghost" size="sm" onClick={() => setEmployeeStatus.mutate({ employeeId: e.id, status: e.status === 'active' ? 'terminated' : 'active' })}>
														<Power className="h-3 w-3" /> {e.status === 'active' ? 'Deactivate' : 'Activate'}
													</Button>
												</div>
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					</>
				)}

				{tab === 'hr' && (
					<>
						{isSuperAdmin && (
							<div className="px-5 py-3 border-b flex items-center justify-end">
								<Button onClick={() => setHrDrawer({ open: true, mode: 'create', user: null })}><Plus className="h-3 w-3" /> Add HR user</Button>
							</div>
						)}
						<UserTable rows={hrUsers?.data as UserRow[] | undefined ?? []} isSuperAdmin={isSuperAdmin} onEdit={(u) => setHrDrawer({ open: true, mode: 'edit', user: u })} onSetActive={(u) => setActive.mutate({ userId: u.id, isActive: !u.isActive })} />
					</>
				)}

				{tab === 'approvers' && (
					<>
						{isSuperAdmin && (
							<div className="px-5 py-3 border-b flex items-center justify-end">
								<Button onClick={() => setApproverDrawer({ open: true, mode: 'create', user: null })}><Plus className="h-3 w-3" /> Add approver</Button>
							</div>
						)}
						<UserTable rows={approvers?.data as UserRow[] | undefined ?? []} isSuperAdmin={isSuperAdmin} onEdit={(u) => setApproverDrawer({ open: true, mode: 'edit', user: u })} onSetActive={(u) => setActive.mutate({ userId: u.id, isActive: !u.isActive })} />
					</>
				)}

				{tab === 'leave' && (
					<table className="w-full text-[13px]">
						<thead>
							<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
								<th className="text-left px-4 py-2.5 font-semibold">Employee</th>
								<th className="text-left px-4 py-2.5 font-semibold">Type</th>
								<th className="text-left px-4 py-2.5 font-semibold">Date range</th>
								<th className="text-left px-4 py-2.5 font-semibold">Approval</th>
								<th className="text-left px-4 py-2.5 font-semibold">Status</th>
								<th className="px-4 py-2.5" />
							</tr>
						</thead>
						<tbody>
							{(leaves?.data ?? []).length === 0 && (
								<tr><td colSpan={6} className="text-center text-muted-foreground p-10">No leave requests yet.</td></tr>
							)}
							{(leaves?.data ?? []).map((r) => (
								<tr key={r.id} className="border-b last:border-0">
									<td className="px-4 py-3.5">
										<div className="flex items-center gap-2.5">
											<Avatar name={r.employee?.fullName || 'Unknown'} />
											<div className="font-medium">{r.employee?.fullName}</div>
										</div>
									</td>
									<td className="px-4 py-3.5">{LABEL_BY_LEAVE_TYPE[r.type as keyof typeof LABEL_BY_LEAVE_TYPE]}</td>
									<td className="px-4 py-3.5">{formatDateRange(r.startDate, r.endDate)}</td>
									<td className="px-4 py-3.5"><ApprovalProgress approvals={r.approvals ?? []} currentLevel={r.currentLevel} /></td>
									<td className="px-4 py-3.5"><Badge tone={TONE_BY_LEAVE_STATUS[r.status]}>{r.status}</Badge></td>
									<td className="px-4 py-3.5 text-right"><Button variant="ghost" size="sm" onClick={() => setDetailLeave(r)}><Eye className="h-3 w-3" /> View</Button></td>
								</tr>
							))}
						</tbody>
					</table>
				)}

				{tab === 'reimbursement' && (
					<table className="w-full text-[13px]">
						<thead>
							<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
								<th className="text-left px-4 py-2.5 font-semibold">Employee</th>
								<th className="text-left px-4 py-2.5 font-semibold">Category</th>
								<th className="text-left px-4 py-2.5 font-semibold">Description</th>
								<th className="text-right px-4 py-2.5 font-semibold">Amount</th>
								<th className="text-left px-4 py-2.5 font-semibold">Approval</th>
								<th className="text-left px-4 py-2.5 font-semibold">Status</th>
								<th className="px-4 py-2.5" />
							</tr>
						</thead>
						<tbody>
							{(claims?.data ?? []).length === 0 && (
								<tr><td colSpan={7} className="text-center text-muted-foreground p-10">No reimbursement claims yet.</td></tr>
							)}
							{(claims?.data ?? []).map((r) => (
								<tr key={r.id} className="border-b last:border-0">
									<td className="px-4 py-3.5">
										<div className="flex items-center gap-2.5">
											<Avatar name={r.employee?.fullName || 'Unknown'} />
											<div className="font-medium">{r.employee?.fullName}</div>
										</div>
									</td>
									<td className="px-4 py-3.5">{LABEL_BY_REIMBURSEMENT_CATEGORY[r.category as keyof typeof LABEL_BY_REIMBURSEMENT_CATEGORY]}</td>
									<td className="px-4 py-3.5">{r.description}</td>
									<td className="px-4 py-3.5 text-right tabular font-semibold">{formatIDR(Number(r.amount))}</td>
									<td className="px-4 py-3.5"><ApprovalProgress approvals={r.approvals ?? []} currentLevel={r.currentLevel} /></td>
									<td className="px-4 py-3.5"><Badge tone={TONE_BY_REIMBURSEMENT_STATUS[r.status]}>{LABEL_BY_REIMBURSEMENT_STATUS[r.status] ?? r.status}</Badge></td>
									<td className="px-4 py-3.5 text-right">
										<div className="inline-flex gap-1.5 justify-end">
											<Button variant="ghost" size="sm" onClick={() => setDetailReimb(r)}><Eye className="h-3 w-3" /> View</Button>
											{isSuperAdmin && r.status === 'sent_to_paid' && (
												<Button size="sm" onClick={() => reimburse.mutate(r.id)} disabled={reimburse.isPending}><Check className="h-3 w-3" /> Reimbursement paid</Button>
											)}
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</Card>

			<UserFormDrawer
				open={hrDrawer.open}
				mode={hrDrawer.mode}
				kind="hr"
				companyId={id}
				companyName={company.name}
				user={hrDrawer.user}
				onClose={() => setHrDrawer({ open: false, mode: 'create', user: null })}
			/>
			<UserFormDrawer
				open={approverDrawer.open}
				mode={approverDrawer.mode}
				kind="approver"
				companyId={id}
				companyName={company.name}
				user={approverDrawer.user}
				onClose={() => setApproverDrawer({ open: false, mode: 'create', user: null })}
			/>
			<EmployeeFormDrawer
				open={employeeDrawer.open}
				mode={employeeDrawer.mode}
				companyId={id}
				companyName={company.name}
				employee={employeeDrawer.employee}
				onClose={() => setEmployeeDrawer({ open: false, mode: 'create', employee: null })}
			/>
		</>
	);
}

function UserTable({
	rows,
	isSuperAdmin,
	onEdit,
	onSetActive
}: {
	rows: UserRow[];
	isSuperAdmin: boolean;
	onEdit: (u: UserRow) => void;
	onSetActive: (u: UserRow) => void;
}) {
	return (
		<table className="w-full text-[13px]">
			<thead>
				<tr className="bg-background border-b text-[11px] uppercase tracking-wider text-muted-foreground">
					<th className="text-left px-4 py-2.5 font-semibold">Name</th>
					<th className="text-left px-4 py-2.5 font-semibold">Email</th>
					<th className="text-left px-4 py-2.5 font-semibold">Department</th>
					<th className="text-left px-4 py-2.5 font-semibold">Position</th>
					<th className="text-left px-4 py-2.5 font-semibold">Status</th>
					<th className="text-left px-4 py-2.5 font-semibold">Created</th>
					{isSuperAdmin && <th />}
				</tr>
			</thead>
			<tbody>
				{rows.length === 0 && (
					<tr><td colSpan={7} className="text-center text-muted-foreground p-10">No users yet.</td></tr>
				)}
				{rows.map((u) => (
					<tr key={u.id} className="border-b last:border-0">
						<td className="px-4 py-3.5 font-medium">{u.name}</td>
						<td className="px-4 py-3.5 text-muted-foreground">{u.email}</td>
						<td className="px-4 py-3.5 text-muted-foreground">{u.department || '—'}</td>
						<td className="px-4 py-3.5 text-muted-foreground">{u.position || '—'}</td>
						<td className="px-4 py-3.5">
							<Badge tone={u.isBlocked ? 'red' : !u.isActive ? 'gray' : 'green'}>
								{u.isBlocked ? 'Blocked' : !u.isActive ? 'Inactive' : 'Active'}
							</Badge>
						</td>
						<td className="px-4 py-3.5 text-muted-foreground">{formatDateShort(u.createdAt)}</td>
						{isSuperAdmin && (
							<td className="px-4 py-3.5 text-right">
								<div className="inline-flex gap-1.5">
									<Button variant="secondary" size="sm" onClick={() => onEdit(u)}>
										<Pencil className="h-3 w-3" /> Edit
									</Button>
									<Button variant="ghost" size="sm" onClick={() => onSetActive(u)}>
										<Power className="h-3 w-3" /> {u.isActive ? 'Deactivate' : 'Activate'}
									</Button>
								</div>
							</td>
						)}
					</tr>
				))}
			</tbody>
		</table>
	);
}
